import Button from '@/shared/Button'
import FormItem from '@/shared/FormCreator/FormItem'
import Download from '@mui/icons-material/Download'
import Preview from '@mui/icons-material/Preview'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import debounce from '@mui/material/utils/debounce'
import isEmpty from 'lodash/isEmpty'
import unionBy from 'lodash/unionBy'
import moment from 'moment'
import { useForm } from 'react-hook-form'

// import usePermissions from '@/hooks/usePermissions'
import ReportDocument from '@/components/ReportPDF/ReportDocument'
import ReportPreview from '@/components/ReportPDF/ReportPreview'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'

import ReportSample from '@/components/ReportPDF/ReportSample'
import { usePDF, pdf } from '@react-pdf/renderer'
import { toPng } from 'html-to-image'
import * as ReactDOMClient from 'react-dom/client'

import { Packer } from 'docx'
import { generate } from '@/components/ReportWord/WordGenerator'
import { saveAs } from 'file-saver'
import PermissionsController from '@/components/PermissionsController'

import PDFDonutChart from '@/components/ReportPDF/PDFDonutChart'
import PDFPieChart from '@/components/ReportPDF/PDFPieChart'
import PDFBarChart2 from '@/components/ReportPDF/PDFBarChart2'
import PDFBarChart3 from '@/components/ReportPDF/PDFBarChart3'
import PDFBarChart4 from '@/components/ReportPDF/PDFBarChart4'

import handleDownload from '@/components/ReportExcel/ExcelDownloader'

import { formatYearMonth } from '../components/ReportPDF/TaiwanDateConvert'

const mapStateToProps = ({ report, loading }) => ({
  reportData: report,
  isReportLoading:
    loading['report/generateMetaData'] ||
    loading['report/getPhishCampaignReport'] ||
    loading['report/getEmailTempLogReport'] ||
    loading['report/getPhishEmailAction'] ||
    loading['report/getCampaignHistory'] ||
    loading['report/getUserPhishDepartment'] ||
    loading['report/getUserPhishOffice'] ||
    loading['report/getTempResultReport'] ||
    loading['report/getOperatingSystemReport'] ||
    loading['report/getUserPhishLogList'] ||
    loading['report/getUserPhishLogTable'],
})

const inputConfig = {
  endAdornment: args => {
    const { value, inputProps: { maxLength } = {} } = args

    return (
      maxLength && (
        <Typography
          variant="caption"
          sx={{ color: '#00000040', fontSize: '14px !important' }}
        >{`${value.length}/${maxLength}`}</Typography>
      )
    )
  },
}

// const DownloadElement = ({ url, loading, setIsDownloading }) => {
//   const ref = useRef()

//   useEffect(() => {
//     if (url && !loading) {
//       ref.current?.click?.()
//       setIsDownloading(false)
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [url, loading])

//   return <div ref={ref} />
// }

const FirstRenderDoc = ({ download, onDownload, ...props }) => {
  usePDF({
    document: <ReportDocument {...props} />,
  })

  return null
}

const loadFirstRenderDoc = ({ onRender, ...props }) => {
  const helperDiv = document.createElement('div')
  const newRoot = ReactDOMClient.createRoot(helperDiv)

  const doc = (
    <FirstRenderDoc
      {...props}
      onRender={onRenderProps => {
        onRender?.(onRenderProps)
        newRoot?.unmount()
        helperDiv?.remove()
      }}
    />
  )

  newRoot.render(<React.StrictMode>{doc}</React.StrictMode>)
}

const loadDonutChart = ({ setDonutChartImg, data }) => {
  const helperDiv = document.createElement('div')
  const newRoot = ReactDOMClient.createRoot(helperDiv)

  const {
    onlyOpenCount,
    onlyLinkCount,
    onlyFileCount,
    openLinkCount,
    openFileCount,
    linkFileCount,
    linkTextCount,
    threeStatesCount,
    phishCount,
  } = data

  const convertData = [
    { name: '僅瀏覽郵件', value: onlyOpenCount },
    { name: '僅點閱連結', value: onlyLinkCount },
    { name: '僅開啟附件', value: onlyFileCount },
    { name: '瀏覽郵件 + 點閱連結', value: openLinkCount },
    { name: '瀏覽郵件 + 開啟附件', value: openFileCount },
    { name: '點閱連結 + 開啟附件', value: linkFileCount },
    { name: '點閱連結 + 輸入資料', value: linkTextCount },
    { name: '三種釣中行為(含)以上', value: threeStatesCount },
  ]

  const chart = (
    <PDFDonutChart
      label={phishCount}
      data={convertData}
      onLoaded={node => {
        if (node) {
          // 確保圖表完全渲染後執行
          setTimeout(() => {
            toPng(node, { height: 650, width: 800 }).then(img => {
              setDonutChartImg?.(img)
              newRoot?.unmount()
              helperDiv?.remove()
            })
          }, 5000)
        }
      }}
    />
  )

  newRoot.render(<React.StrictMode>{chart}</React.StrictMode>)
}

const loadPieChart = ({ setPieChartImg, data }) => {
  const helperDiv = document.createElement('div')
  const newRoot = ReactDOMClient.createRoot(helperDiv)

  const convertData = data.map(group => ({
    name: group.groupName,
    value: group.phishCount,
  }))

  const chart = (
    <PDFPieChart
      data={convertData}
      onLoaded={node => {
        if (node) {
          // 確保圖表完全渲染後執行
          setTimeout(() => {
            toPng(node, { height: 450, width: 700 }).then(img => {
              setPieChartImg?.(img)
              newRoot?.unmount()
              helperDiv?.remove()
            })
          }, 5000)
        }
      }}
    />
  )

  newRoot.render(<React.StrictMode>{chart}</React.StrictMode>)
}

const loadPieChart2 = ({ setPieChart2Img, data }) => {
  const helperDiv = document.createElement('div')
  const newRoot = ReactDOMClient.createRoot(helperDiv)
  const convertData = data.map(group => ({
    name: group.osName,
    value: group.useCount,
  }))

  const chart = (
    <PDFPieChart
      data={convertData}
      onLoaded={node => {
        if (node) {
          // 確保圖表完全渲染後執行
          setTimeout(() => {
            toPng(node, { height: 450, width: 700 }).then(img => {
              setPieChart2Img?.(img)
              newRoot?.unmount()
              helperDiv?.remove()
            })
          }, 5000)
        }
      }}
    />
  )

  newRoot.render(<React.StrictMode>{chart}</React.StrictMode>)
}

const loadBarChart2 = ({ setBarChart2Img, data }) => {
  const helperDiv = document.createElement('div')
  const newRoot = ReactDOMClient.createRoot(helperDiv)
  const convertData = data.map(item => ({
    scheduledStartTime: item.year,
    groupNumber: item.report.userCount,
    openEmail: item.report.openCount,
    linkedClicked: item.report.linkCount,
    attachmentOpened: item.report.fileCount,
    enterData: item.report.textCount,
  }))

  const chart = (
    <PDFBarChart2
      data={convertData}
      onLoaded={node => {
        if (node) {
          // 確保圖表完全渲染後執行
          setTimeout(() => {
            toPng(node, { height: 650, width: 800 }).then(img => {
              setBarChart2Img?.(img)
              newRoot?.unmount()
              helperDiv?.remove()
            })
          }, 5000)
        }
      }}
    />
  )

  newRoot.render(<React.StrictMode>{chart}</React.StrictMode>)
}

const loadBarChart3 = ({ setBarChart3Img, data }) => {
  const helperDiv = document.createElement('div')
  const newRoot = ReactDOMClient.createRoot(helperDiv)
  const convertData = data.map(item => ({
    groupNumber: item.report.userCount,
    openEmail: item.report.openCount,
    linkedClicked: item.report.linkCount,
    attachmentOpened: item.report.fileCount,
    enterData: item.report.textCount,
    tempId: item.tempId,
  }))

  const chart = (
    <PDFBarChart3
      data={convertData}
      onLoaded={node => {
        if (node) {
          // 確保圖表完全渲染後執行
          setTimeout(() => {
            toPng(node, { height: 650, width: 800 }).then(img => {
              setBarChart3Img?.(img)
              newRoot?.unmount()
              helperDiv?.remove()
            })
          }, 5000)
        }
      }}
    />
  )

  newRoot.render(<React.StrictMode>{chart}</React.StrictMode>)
}

const loadBarChart4 = ({ setBarChart4Img, data }) => {
  const helperDiv = document.createElement('div')
  const newRoot = ReactDOMClient.createRoot(helperDiv)
  const convertData = data.map(item => ({
    totalNumber: item.phishCount + item.safeCount,
    notCaught: item.safeCount,
    caught: item.phishCount,
    depart: item.groupName,
  }))

  const chart = (
    <PDFBarChart4
      data={convertData}
      onLoaded={node => {
        if (node) {
          // 確保圖表完全渲染後執行
          setTimeout(() => {
            toPng(node, { height: 650, width: 800 }).then(img => {
              setBarChart4Img?.(img)
              newRoot?.unmount()
              helperDiv?.remove()
            })
          }, 5000)
        }
      }}
    />
  )

  newRoot.render(<React.StrictMode>{chart}</React.StrictMode>)
}

const PhishReportPage = ({ dispatch, isReportLoading, reportData }) => {
  const { t } = useTranslation()

  const {
    actionGroup,
    reprotHistories,
    tempResults,
    deptReportGroups,
    officeReportGroups,
    osCounts,
    reprotDao,
  } = reportData || {}

  const { campaignStartDate } = reprotDao || {}

  const phishCampaignsSearchInputRef = useRef()

  const [showSample, setShowSample] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [phishCampaignsSearchInput, setPhishCampaignsSearchInput] = useState('')
  const [phishCampaignsSearchResults, setPhishCampaignsSearchResults] = useState({})
  const [phishCampaignsPage, setPhishCampaignsPage] = useState(0)

  const [pdfDoc, setPdfDoc] = useState(null)
  const [isDownloading, setIsDownloading] = useState()
  const [isDownloadingWord, setIsDownloadingWord] = useState()
  const [isDownloadingExcel, setIsDownloadingExcel] = useState()

  const [donutChartImg, setDonutChartImg] = useState(null)
  const [pieChartImg, setPieChartImg] = useState(null)
  const [barChart2Img, setBarChart2Img] = useState(null)
  const [barChart3Img, setBarChart3Img] = useState(null)
  const [barChart4Img, setBarChart4Img] = useState(null)
  const [pieChart2Img, setPieChart2Img] = useState(null)

  const { data: phishCampaignsSearchResultsData = [], paging: phishCampaignsSearchResultsPaging } =
    phishCampaignsSearchResults

  const validations = {
    primaryCompanyFullname: Yup.string().required(),
    // primaryCompanyNickname: Yup.string().required(),
    secondaryCompanyFullname: Yup.string().required(),
    // secondaryCompanyNickname: Yup.string().required(),
    phishCampaign: Yup.object().required(),
    // prepDays: Yup.string().required(),
    serviceCategory: Yup.string().required(),
    version: Yup.string().required(),
    // testCount: Yup.string().required(),
  }

  const form = useForm({
    resolver: yupResolver(Yup.object().shape(validations)),
    mode: 'onBlur',
  })

  const {
    control,
    handleSubmit,
    watch,
    trigger,
    setValue,
    // eslint-disable-next-line no-unused-vars
    formState: { errors },
  } = form

  const phishType = watch('phishType')
  const primaryCompanyNickname = watch('primaryCompanyNickname')
  const secondaryCompanyNickname = watch('secondaryCompanyNickname')
  const secondaryCompanyFullname = watch('secondaryCompanyFullname')
  const generateStartDate = watch('generateStartDate')
  const generateEndDate = watch('generateEndDate')

  // const canOwner = usePermissions('account_create')

  // 演練活動下拉選單：email 走輕量查詢（lite，不算統計欄位）；以遞增序號忽略較舊請求的晚到回應，
  // 避免快速輸入時舊結果覆蓋新結果
  const campRequestSeqRef = useRef(0)
  const [isCampOptionsLoading, setIsCampOptionsLoading] = useState(false)

  const fetchPhishCampaigns = useCallback(() => {
    campRequestSeqRef.current += 1
    const seq = campRequestSeqRef.current
    setIsCampOptionsLoading(true)
    dispatch({
      type: `phishCampaign/${
        phishType === 'S' ? 'getPhishSmsCampaigns' : 'getPhishCampaignOptions'
      }`,
      errorCallback: () => {
        if (seq === campRequestSeqRef.current) setIsCampOptionsLoading(false)
      },
      payload: {
        pageIndex: phishCampaignsPage,
        pageSize: 10,
        searchCampName: phishCampaignsSearchInput,
        // searchCampInitialTime: moment().add(-1, 'year').toDate(),
        // searchCampEndTime: moment().add(1, 'year').toDate(),
        searchCampInitialTime: moment('2023-08-01', 'YYYY-MM-DD').toDate(),
        searchCampEndTime: moment().toDate(),
        sort: 'CREATED_TIME_DESC',
        status: 'COMPLETE',
        runType: 'main',
      },
      successCallback: response => {
        if (seq !== campRequestSeqRef.current) return
        setIsCampOptionsLoading(false)
        const { data: responseData = [], paging: responsePaging = {} } = response || {}
        const { pageIndex } = responsePaging

        const newData = responseData.map(d => ({
          id: d.campaignMainId,
          label: d.campaignMainName,
          data: d,
        }))

        // 更新資料並顯示在選單上
        setPhishCampaignsSearchResults(s => ({
          data: pageIndex > 0 ? unionBy(s.data || [], newData, 'id') : newData,
          paging: responsePaging,
        }))
      },
    })
  }, [dispatch, phishCampaignsPage, phishCampaignsSearchInput, phishType])

  // debounce 函式須只建立一次：原本每次輸入都 new 一個 debounce，等於沒有 debounce，每個字元都會發一次查詢
  const loadMorePhishCampaignsDebounce = useMemo(
    () =>
      debounce(() => {
        setPhishCampaignsPage(p => p + 1)
      }, 400),
    [],
  )

  const campSearchDebounce = useMemo(
    () =>
      debounce(({ value, reason }) => {
        phishCampaignsSearchInputRef.current = value

        if (['input', 'clear'].includes(reason)) {
          setPhishCampaignsPage(0)
          setPhishCampaignsSearchInput(value)
        }
      }, 400),
    [],
  )

  const onSubmit = (data, callback) => {
    const { phishCampaign, primaryCompanyFullname, version, startDate, endDate } = data
    const { id: campaignId } = phishCampaign || {}

    setDonutChartImg(null)
    setPieChartImg(null)
    setPieChart2Img(null)
    setBarChart2Img(null)
    setBarChart3Img(null)
    setBarChart4Img(null)
    setPdfDoc(null)

    if (campaignId) {
      const actions = [
        {
          type: 'report/getEmailReprotWithReportDetail',
          payload: {
            campaignId,
            companyName: primaryCompanyFullname,
            version,
            reportDate: null,
          },
        },
        { type: 'report/getPhishCampaignReport', payload: { campaignId } },
        { type: 'report/getEmailTempLogReport', payload: { campaignId } },
        { type: 'report/getPhishEmailAction', payload: { campaignId } },
        { type: 'report/getPhishCampaignHistory', payload: { campaignId, startDate, endDate } },
        { type: 'report/getUserPhishDepartment', payload: { campaignId } },
        { type: 'report/getUserPhishOffice', payload: { campaignId } },
        { type: 'report/getTempResultReport', payload: { campaignId } },
        { type: 'report/getOperatingSystemReport', payload: { campaignId } },
        { type: 'report/getUserPhishLogList', payload: { campaignId } },
        { type: 'report/getUserPhishLogTable', payload: { campaignId } },
      ]

      actions
        .reduce(
          (prevAction, { type, payload }) =>
            prevAction.then(
              () =>
                new Promise(resolve => {
                  dispatch({
                    type,
                    payload,
                    successCallback: resolve,
                  })
                }),
            ),
          Promise.resolve(),
        )
        .then(() => {
          callback?.()
        })
    }
  }

  const primaryCompanyFullname = watch('primaryCompanyFullname')

  // const downloadExcel = async ({data}) => {
  //   console.log(data);

  //   const blob = await handleDownload()
  //   saveAs(blob, `演練結果_${new Date().toISOString().slice(0, 10)}.xlsx`)
  // }

  const generateWordDocument = async () => {
    try {
      const wordDoc = await generate({
        donutChartImg,
        pieChartImg,
        pieChart2Img,
        barChart2Img,
        barChart3Img,
        barChart4Img,
        reportData: {
          ...reportData,
          phishType: t(`general.${phishType === 'M' ? 'mail' : 'sms'}`),
          primaryCompanyNickname,
          secondaryCompanyNickname,
          secondaryCompanyFullname,
          generateStartDate,
          generateEndDate,
        },
      })
      const blob = await Packer.toBlob(wordDoc)

      // OO股份有限公司_社交工程郵件演練服務演練結果報告書_YYYYMM
      saveAs(
        blob,
        `${primaryCompanyFullname}_社交工程郵件演練服務演練結果報告書_${formatYearMonth(
          campaignStartDate,
        )}.docx`,
      )

      await handleDownload({
        reportData: {
          ...reportData,
          phishType: t(`general.${phishType === 'M' ? 'mail' : 'sms'}`),
          campaignStartDate,
        },
      })
    } catch (error) {
      console.error('Error generating Word document:', error)
    }
  }

  useEffect(() => {
    setValue('serviceCategory', t(`general.${phishType === 'M' ? 'mail' : 'sms'}`))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phishType])

  // fetch email templates START
  // 搜尋字串／分頁／活動類別變更時重新查詢（搜尋字串已於 campSearchDebounce 防抖）
  useEffect(() => {
    fetchPhishCampaigns()
  }, [fetchPhishCampaigns])
  // fetch email templates END

  // email templates clean up START
  useEffect(() => {
    setPhishCampaignsSearchResults({})
    setPhishCampaignsPage(0)
  }, [])
  // email templates clean up END

  useEffect(() => {
    const loadDonutChartData = async () => {
      if (actionGroup) {
        await loadDonutChart({ setDonutChartImg, data: actionGroup })
      }
    }

    loadDonutChartData()
  }, [actionGroup]) // 監聽 actionGroup 資料變動

  useEffect(() => {
    const loadPieChartData = async () => {
      if (officeReportGroups) {
        await loadPieChart({ setPieChartImg, data: officeReportGroups })
      }
    }

    loadPieChartData()
  }, [officeReportGroups]) // 監聽 officeReportGroups 資料變動

  useEffect(() => {
    const loadPieChart2Data = async () => {
      if (osCounts) {
        await loadPieChart2({ setPieChart2Img, data: osCounts })
      }
    }

    loadPieChart2Data()
  }, [osCounts]) // 監聽 osCounts 資料變動

  useEffect(() => {
    const loadBarChart2Data = async () => {
      if (reprotHistories) {
        await loadBarChart2({ setBarChart2Img, data: reprotHistories })
      }
    }

    loadBarChart2Data()
  }, [reprotHistories]) // 監聽 reprotHistories 資料變動

  useEffect(() => {
    const loadBarChart3Data = async () => {
      if (tempResults) {
        await loadBarChart3({ setBarChart3Img, data: tempResults })
      }
    }

    loadBarChart3Data()
  }, [tempResults]) // 監聽 tempResults 資料變動

  useEffect(() => {
    const loadBarChart4Data = async () => {
      if (deptReportGroups) {
        await loadBarChart4({ setBarChart4Img, data: deptReportGroups })
      }
    }

    loadBarChart4Data()
  }, [deptReportGroups]) // 監聽 deptReportGroups 資料變動

  useEffect(() => {
    trigger?.()
  }, [trigger])

  useEffect(() => {
    if (
      reportData &&
      donutChartImg &&
      pieChartImg &&
      pieChart2Img &&
      barChart2Img &&
      barChart3Img &&
      barChart4Img &&
      isDownloadingWord
    ) {
      generateWordDocument()
      setIsDownloadingWord(false)
      return
    }

    if (
      reportData &&
      donutChartImg &&
      pieChartImg &&
      pieChart2Img &&
      barChart2Img &&
      barChart3Img &&
      barChart4Img &&
      !pdfDoc
    ) {
      loadFirstRenderDoc({
        donutChartImg,
        pieChartImg,
        pieChart2Img,
        barChart2Img,
        barChart3Img,
        barChart4Img,
        reportData: {
          ...reportData,
          phishType: t(`general.${phishType === 'M' ? 'mail' : 'sms'}`),
        },
        onRender: ({ pageData }) => {
          setPdfDoc(
            <ReportDocument
              donutChartImg={donutChartImg}
              pieChartImg={pieChartImg}
              pieChart2Img={pieChart2Img}
              barChart2Img={barChart2Img}
              barChart3Img={barChart3Img}
              barChart4Img={barChart4Img}
              pageData={pageData}
              reportData={{
                ...reportData,
                phishType: t(`general.${phishType === 'M' ? 'mail' : 'sms'}`),
                primaryCompanyNickname,
                secondaryCompanyNickname,
                secondaryCompanyFullname,
                generateStartDate,
                generateEndDate,
              }}
            />,
          )
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    donutChartImg,
    pieChartImg,
    pieChart2Img,
    barChart2Img,
    barChart3Img,
    barChart4Img,
    pdfDoc,
    isDownloadingWord,
  ])

  useEffect(() => {
    const downloadAll = async () => {
      if (isDownloading && pdfDoc) {
        try {
          // Step 1: 生成 PDF Blob 並下載
          const blob = await pdf(pdfDoc).toBlob()

          saveAs(
            blob,
            // OO股份有限公司_社交工程郵件演練服務演練結果報告書_YYYYMM
            `${primaryCompanyFullname}_社交工程郵件測試服務結果及分析報告書_${formatYearMonth(
              campaignStartDate,
            )}.pdf`,
          )

          // Step 2: 再下載 Excel
          await handleDownload({
            reportData: {
              ...reportData,
              phishType: t(`general.${phishType === 'M' ? 'mail' : 'sms'}`),
              campaignStartDate,
            },
          })
        } catch (e) {
          console.error('下載失敗：', e)
        } finally {
          setIsDownloading(false)
        }
      }
    }

    downloadAll()
  }, [
    campaignStartDate,
    generateStartDate,
    isDownloading,
    pdfDoc,
    phishType,
    primaryCompanyFullname,
    reportData,
    t,
  ])

  useEffect(() => {
    const downloadExcel = async () => {
      if (isDownloadingExcel) {
        try {
          await handleDownload({
            reportData: {
              ...reportData,
              phishType: t(`general.${phishType === 'M' ? 'mail' : 'sms'}`),
              campaignStartDate,
            },
          })
        } catch (e) {
          console.error('下載失敗：', e)
        } finally {
          setIsDownloadingExcel(false)
        }
      }
    }

    downloadExcel()
  }, [
    campaignStartDate,
    generateStartDate,
    isDownloadingExcel,
    phishType,
    primaryCompanyFullname,
    reportData,
    t,
  ])

  return (
    <Container>
      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button
          disabled={isReportLoading || isDownloading || isDownloadingWord}
          variant="text"
          color="secondary_80"
          endIcon={<Preview />}
          onClick={() => {
            setShowSample(true)
          }}
        >
          {t('general.preview')}
        </Button>

        <Button
          loading={isReportLoading || isDownloading || isDownloadingWord}
          disabled={!isEmpty(errors) || isReportLoading || isDownloading || isDownloadingWord}
          variant="text"
          color="secondary_80"
          endIcon={<Preview />}
          onClick={handleSubmit(data => {
            onSubmit(data, () => {
              setShowPreview(true)
            })
          })}
        >
          {t('general.review_document')}
        </Button>

        <PermissionsController permission="phishReport_download">
          <Button
            onClick={handleSubmit(data => {
              onSubmit(data, () => {
                setIsDownloading(true)
              })
            })}
            loading={isReportLoading || isDownloading || isDownloadingWord}
            disabled={!isEmpty(errors) || isReportLoading || isDownloading || isDownloadingWord}
            variant="text"
            color="secondary_80"
            endIcon={<Download />}
          >
            {t('general.download_pdf')}
          </Button>
        </PermissionsController>

        <PermissionsController permission="phishReport_download">
          <Button
            onClick={handleSubmit(data => {
              onSubmit(data, () => {
                setIsDownloadingWord(true)
              })
            })}
            loading={isReportLoading || isDownloading || isDownloadingWord}
            disabled={!isEmpty(errors) || isReportLoading || isDownloading || isDownloadingWord}
            variant="text"
            color="secondary_80"
            endIcon={<Download />}
          >
            {t('general.download_word')}
          </Button>
        </PermissionsController>

        <PermissionsController permission="phishReport_download">
          <Button
            onClick={handleSubmit(data => {
              onSubmit(data, () => {
                setIsDownloadingExcel(true)
              })
            })}
            loading={isReportLoading || isDownloading || isDownloadingWord}
            disabled={!isEmpty(errors) || isReportLoading || isDownloading || isDownloadingWord}
            variant="text"
            color="secondary_80"
            endIcon={<Download />}
          >
            {t('general.download_excel')}
          </Button>
        </PermissionsController>

        {/* {isDownloading && pdfDoc && (
          <PDFDownloadLink
            document={pdfDoc}
            fileName={`${primaryCompanyFullname}_社交工程郵件測試服務結果及分析報告書_PRO版_V2.pdf`}
          >
            {props => <DownloadElement {...props} setIsDownloading={setIsDownloading} />}
          </PDFDownloadLink>
        )} */}
      </Stack>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2} mb={4}>
          <Stack direction={'row'} spacing={2}>
            <FormItem
              defaultValue={'M'}
              label={t('general.phish_type')}
              name="phishType"
              type="select"
              control={control}
              inputConfig={{
                sx: { width: 100 },
                options: [
                  { value: 'M', label: t('general.mail') },
                  // { value: 'S', label: t('general.sms') },
                ],
              }}
            />

            <FormItem
              defaultValue={''}
              label={t('menu.phish_campaign')}
              name="phishCampaign"
              type="autocomplete"
              control={control}
              inputConfig={{
                sx: { width: 250 },
                onInputChange: (_, value, reason) => {
                  campSearchDebounce({ value, reason })
                },
                // 已有資料時不重查（原本每次聚焦都重打一次清單 API）
                onFocus: () => {
                  if (!isCampOptionsLoading && phishCampaignsSearchResultsData.length === 0) {
                    fetchPhishCampaigns()
                  }
                },
                onLoadMore: () => {
                  const { hasNext } = phishCampaignsSearchResultsPaging || {}

                  if (!isCampOptionsLoading && hasNext) {
                    loadMorePhishCampaignsDebounce()
                  }
                },
                loading: isCampOptionsLoading,
                loadingText: '載入中…',
                noOptionsText: isCampOptionsLoading ? '載入中…' : t('errors.no_options'),
                options: phishCampaignsSearchResultsData,
                filterOptions: opts => opts,
                isOptionEqualToValue: (option, value) => option.id === value.id,
                renderOption: (renderProps, option) => (
                  <Box component="li" {...renderProps} key={option.id}>
                    {option.label}
                  </Box>
                ),
              }}
            />
          </Stack>

          <Stack direction={'row'} spacing={2}>
            <FormItem
              defaultValue=""
              label={t('general.primary_company_nickname')}
              name="primaryCompanyNickname"
              control={control}
              inputConfig={{ ...inputConfig, inputProps: { maxLength: 40 } }}
            />

            <FormItem
              defaultValue=""
              label={t('general.primary_company_fullname')}
              name="primaryCompanyFullname"
              control={control}
              inputConfig={{ ...inputConfig, inputProps: { maxLength: 40 } }}
            />
          </Stack>

          <Stack direction={'row'} spacing={2}>
            <FormItem
              defaultValue=""
              label={t('general.secondary_company_nickname')}
              name="secondaryCompanyNickname"
              control={control}
              inputConfig={{ ...inputConfig, inputProps: { maxLength: 40 } }}
            />

            <FormItem
              defaultValue=""
              label={t('general.secondary_company_fullname')}
              name="secondaryCompanyFullname"
              control={control}
              inputConfig={{ ...inputConfig, inputProps: { maxLength: 40 } }}
            />
          </Stack>

          <Stack direction={'row'} spacing={2}>
            <FormItem
              defaultValue=""
              disabled
              label={t('general.service_category')}
              name="serviceCategory"
              control={control}
              inputConfig={{ ...inputConfig }}
            />
            <FormItem
              defaultValue=""
              label={t('general.generate_version')}
              name="version"
              control={control}
              inputConfig={{ ...inputConfig, inputProps: { maxLength: 40 } }}
            />
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            <FormItem
              label={t('general.generate_report_date')}
              name="generateStartDate"
              defaultValue={moment().add(-1, 'day').toDate()}
              type="date"
              control={control}
              errors={errors}
            />
            <Typography>{t('general.to')}</Typography>
            <FormItem
              name="generateEndDate"
              defaultValue={moment().toDate()}
              type="date"
              control={control}
              errors={errors}
            />
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            <FormItem
              label={t('general.history_time')}
              name="startDate"
              defaultValue={moment().add(-1, 'year').add(1, 'day').toDate()}
              type="date"
              control={control}
            />
            <Typography>{t('general.to')}</Typography>
            <FormItem
              name="endDate"
              defaultValue={moment().toDate()}
              type="date"
              control={control}
            />
          </Stack>
        </Stack>
      </form>

      {showSample && (
        <ReportPreview
          content={
            <ReportSample
              phishType={phishType}
              generateStartDate={generateStartDate}
              generateEndDate={generateEndDate}
            />
          }
          onClose={() => {
            setShowSample(false)
          }}
        />
      )}

      {showPreview && (
        <ReportPreview
          document={pdfDoc}
          onClose={() => {
            setDonutChartImg(null)
            setPieChartImg(null)
            setPieChart2Img(null)
            setBarChart2Img(null)
            setBarChart3Img(null)
            setBarChart4Img(null)
            setPdfDoc(null)
            setShowPreview(false)
          }}
        />
      )}
    </Container>
  )
}

export default connect(mapStateToProps)(PhishReportPage)
